import { FunctionComponent, useState } from 'react';
import { TextField, IconButton, MenuItem } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { GetAssetTypeDTO } from '../../api/location-start-balance/location-start-balance.contracts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from '@/lib';

export interface StartBalanceEntry {
  id: string; // temporary ID for UI tracking
  assetTypeId: number | null;
  amount: number;
  startDate: Dayjs | null;
}

interface StartBalanceEntryRowProps {
  entry: StartBalanceEntry;
  availableAssetTypes: GetAssetTypeDTO[];
  onChange: (id: string, updatedEntry: Partial<StartBalanceEntry>) => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
}

export const StartBalanceEntryRow: FunctionComponent<StartBalanceEntryRowProps> = ({
  entry,
  availableAssetTypes,
  onChange,
  onDelete,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [dateError, setDateError] = useState<string>('');

  const selectedAssetType = availableAssetTypes.find((at) => at.id === entry.assetTypeId);
  // If there's a first transaction date, use that as max, otherwise use yesterday
  const maxDate = selectedAssetType?.firstLocationTransactionDate
    ? dayjs(selectedAssetType.firstLocationTransactionDate)
    : dayjs().subtract(1, 'day');

  const handleAssetTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const assetTypeId = Number(event.target.value);
    const assetType = availableAssetTypes.find((at) => at.id === assetTypeId);

    // Set default date to the earliest possible date (first transaction date or yesterday)
    let defaultDate = dayjs().subtract(1, 'day'); // Default to yesterday
    if (assetType?.firstLocationTransactionDate) {
      const firstTransactionDate = dayjs(assetType.firstLocationTransactionDate);
      defaultDate = firstTransactionDate;
    }

    onChange(entry.id, {
      assetTypeId,
      startDate: defaultDate,
    });
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      onChange(entry.id, { amount: 0 });
    } else {
      const amount = parseInt(value, 10);
      if (!isNaN(amount) && amount >= 0) {
        onChange(entry.id, { amount });
      }
    }
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    setDateError('');

    if (newDate && maxDate && newDate.isAfter(maxDate)) {
      setDateError(t('start_date_cannot_be_after_first_transaction', { date: maxDate.format('DD/MM/YYYY') }));
      return;
    }

    onChange(entry.id, { startDate: newDate });
  };

  return (
    <>
      <div className="flex items-start gap-4 my-2">
        <TextField
          select
          label={t('asset_type')}
          value={entry.assetTypeId || ''}
          onChange={handleAssetTypeChange}
          disabled={disabled}
          size="small"
          sx={{ flex: 1 }}
          required
        >
          {availableAssetTypes.map((assetType) => (
            <MenuItem key={assetType.id} value={assetType.id}>
              {assetType.name} ({assetType.code})
            </MenuItem>
          ))}
        </TextField>

        <DatePicker
          label={t('start_date')}
          value={entry.startDate}
          onChange={handleDateChange}
          disabled={disabled || !entry.assetTypeId}
          maxDate={maxDate}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 200 },
              required: true,
              error: !!dateError,
              helperText: dateError || (maxDate && !disabled ? t('max_date', { date: maxDate.format('DD/MM/YYYY') }) : ''),
            },
          }}
        />

        <TextField
          label={t('amount')}
          type="number"
          value={entry.amount}
          onChange={handleAmountChange}
          disabled={disabled}
          size="small"
          inputProps={{ min: 1 }}
          sx={{ width: 150 }}
          required
        />

        <IconButton
          onClick={() => onDelete(entry.id)}
          disabled={disabled}
          size="small"
          className={`text-red-600 dark:text-red-400 ${disabled ? 'hidden' : ''} mt-[0.65rem]`}
        >
          <Delete />
        </IconButton>
      </div>
    </>
  );
};
